import React, { useState, useCallback } from 'react';
import {
  detectAnomaly,
  trainAnomalyEnsemble,
  getAnomalyHistory,
} from '../../../services/patternOfLife.service';
import AnomalyAlertBanner from '../AnomalyAlertBanner';
import { MlRiskScorePanel } from '../RiskScoring';
import {
  ZoneExposureSummary,
  ZoneVisitHistory,
  ZoneVisitMap,
} from '../HighRiskZones';
import { NetworkIntelligencePanel } from '../NetworkIntelligence';
import { FeatureInfoButton } from '../FeatureDocumentation';

// Alert level colors and labels
const ALERT_LEVEL_CONFIG = {
  none: { color: '#22C55E', bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'None' },
  low: { color: '#F59E0B', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', label: 'Low' },
  medium: { color: '#F97316', bgColor: 'bg-orange-100', textColor: 'text-orange-800', label: 'Medium' },
  high: { color: '#EF4444', bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'High' },
};

const PolRiskTab = ({ personId }) => {
  // Local error state
  const [error, setError] = useState(null);

  // Anomaly detection state
  const [anomalyResult, setAnomalyResult] = useState(null);
  const [anomalyHistory, setAnomalyHistory] = useState(null);
  const [isTrainingEnsemble, setIsTrainingEnsemble] = useState(false);
  const [isDetectingAnomaly, setIsDetectingAnomaly] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [trainingResult, setTrainingResult] = useState(null);
  const [anomalyLat, setAnomalyLat] = useState('');
  const [anomalyLon, setAnomalyLon] = useState('');


  // ==================== ANOMALY HANDLERS ====================

  const handleTrainEnsemble = useCallback(async () => {
    if (!personId) {
      setError('Please enter a Person ID');
      return;
    }

    setIsTrainingEnsemble(true);
    setError(null);
    setTrainingResult(null);

    try {
      const result = await trainAnomalyEnsemble(personId);
      if (result) {
        setTrainingResult(result);
        if (result.status === 'error') {
          setError(result.error || 'Training failed');
        }
      } else {
        setError('Failed to train anomaly ensemble');
      }
    } catch (err) {
      setError('Failed to train anomaly ensemble');
      console.error(err);
    } finally {
      setIsTrainingEnsemble(false);
    }
  }, [personId]);

  const handleDetectAnomaly = useCallback(async () => {
    if (!personId) {
      setError('Please enter a Person ID');
      return;
    }

    setIsDetectingAnomaly(true);
    setError(null);
    setAnomalyResult(null);

    try {
      const lat = anomalyLat ? parseFloat(anomalyLat) : undefined;
      const lon = anomalyLon ? parseFloat(anomalyLon) : undefined;
      const result = await detectAnomaly(personId, lat, lon);
      if (result) {
        setAnomalyResult(result);
      } else {
        setError('Failed to detect anomaly. Ensure ensemble model is trained.');
      }
    } catch (err) {
      setError('Failed to detect anomaly');
      console.error(err);
    } finally {
      setIsDetectingAnomaly(false);
    }
  }, [personId, anomalyLat, anomalyLon]);

  const handleGetAnomalyHistory = useCallback(async () => {
    if (!personId) {
      setError('Please enter a Person ID');
      return;
    }

    setIsLoadingHistory(true);
    setError(null);
    setAnomalyHistory(null);

    try {
      const result = await getAnomalyHistory(personId, 20);
      if (result) {
        setAnomalyHistory(result);
      } else {
        setError('No anomaly history found');
      }
    } catch (err) {
      setError('Failed to load anomaly history');
      console.error(err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [personId]);

  return (
    <div className="space-y-8">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ==================== SECTION 1: RISK ASSESSMENT ==================== */}
      <section id="risk-assessment">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          Risk Assessment
          <FeatureInfoButton feature="risk" label="Docs" />
        </h2>
        <div className="space-y-6">
          {personId ? (
            <MlRiskScorePanel
              alienId={parseInt(personId, 10)}
              showTrend={true}
              showFactors={true}
              compact={false}
            />
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <h3 className="text-xl font-semibold mb-2">Risk Assessment</h3>
              <p className="text-gray-500 mb-4">
                Enter a Person ID above to view their risk score, factor breakdown,
                and trend analysis.
              </p>
            </div>
          )}

          {/* Risk Scoring Reference */}
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <h4 className="font-semibold text-orange-800 mb-2">Risk Score Categories</h4>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Low: 0-25</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span>Medium: 25-50</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span>High: 50-75</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>Critical: 75-100</span>
              </div>
            </div>
            <h4 className="font-semibold text-orange-800 mt-4 mb-2">Contributing Factors</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
              <div><strong>Anomaly Frequency:</strong> Rate of detected anomalies (30%)</div>
              <div><strong>Compliance:</strong> Rule violations and compliance issues (30%)</div>
              <div><strong>Pattern Stability:</strong> Deviation from established routines (25%)</div>
              <div><strong>Recency Factor:</strong> Time-weighted recent activity (15%)</div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Note: Association Risk factor is a placeholder for future implementation.
            </p>
          </div>
        </div>
      </section>

      {/* ==================== SECTION 2: UNUSUAL ACTIVITY ==================== */}
      <section id="unusual-activity">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          Unusual Activity
          <FeatureInfoButton feature="anomaly" label="Docs" />
        </h2>
        <div className="space-y-6">
          {/* Train Ensemble Section */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">Train Anomaly Ensemble</h3>
            <p className="text-sm text-gray-500 mb-4">
              Train the multi-model ensemble (IsolationForest, AutoEncoder, Statistical) on the person's location history.
              This must be done before anomaly detection will work.
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={handleTrainEnsemble}
                disabled={isTrainingEnsemble || !personId}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isTrainingEnsemble ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Training...
                  </span>
                ) : (
                  'Train Ensemble'
                )}
              </button>
              {trainingResult && (
                <div className={`text-sm ${trainingResult.status === 'trained' ? 'text-green-600' : 'text-red-600'}`}>
                  {trainingResult.status === 'trained' ? (
                    <span>Trained with {trainingResult.samplesUsed} samples</span>
                  ) : (
                    <span>Training failed: {trainingResult.error}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Detection Section */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">Detect Anomaly</h3>
            <p className="text-sm text-gray-500 mb-4">
              Check if a location/time combination is anomalous. Leave coordinates empty to use the latest check-in.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Latitude (optional)</label>
                <input
                  type="number"
                  step="any"
                  value={anomalyLat}
                  onChange={(e) => setAnomalyLat(e.target.value)}
                  placeholder="e.g., 33.4484"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Longitude (optional)</label>
                <input
                  type="number"
                  step="any"
                  value={anomalyLon}
                  onChange={(e) => setAnomalyLon(e.target.value)}
                  placeholder="e.g., -112.0740"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            <button
              onClick={handleDetectAnomaly}
              disabled={isDetectingAnomaly || !personId}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isDetectingAnomaly ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Detecting...
                </span>
              ) : (
                anomalyLat || anomalyLon ? 'Detect Anomaly' : 'Detect at Latest Location'
              )}
            </button>

            {/* Anomaly Result Display */}
            {anomalyResult && (
              <div className="mt-4">
                <AnomalyAlertBanner anomaly={anomalyResult} />

                {/* Raw JSON for debugging */}
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                    View Raw Response
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-64">
                    {JSON.stringify(anomalyResult, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>

          {/* History Section */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">Anomaly History</h3>
            <p className="text-sm text-gray-500 mb-4">
              View recent anomaly detection results for this person.
            </p>
            <button
              onClick={handleGetAnomalyHistory}
              disabled={isLoadingHistory || !personId}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed mb-4"
            >
              {isLoadingHistory ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Loading...
                </span>
              ) : (
                'Load History'
              )}
            </button>

            {anomalyHistory && (
              <div>
                <div className="text-sm text-gray-600 mb-3">
                  Found {anomalyHistory.count} records
                </div>
                {anomalyHistory.anomalies?.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-auto">
                    {anomalyHistory.anomalies.map((item, idx) => (
                      <div
                        key={item.id || idx}
                        className={`p-3 rounded-lg border ${
                          item.isAnomaly
                            ? item.alertLevel === 'high'
                              ? 'border-red-300 bg-red-50'
                              : item.alertLevel === 'medium'
                              ? 'border-orange-300 bg-orange-50'
                              : 'border-yellow-300 bg-yellow-50'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${item.isAnomaly ? 'text-red-600' : 'text-green-600'}`}>
                              {item.isAnomaly ? 'ANOMALY' : 'Normal'}
                            </span>
                            {item.anomalyType && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                {item.anomalyType}
                              </span>
                            )}
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                            item.alertLevel === 'high' ? 'bg-red-200 text-red-800' :
                            item.alertLevel === 'medium' ? 'bg-orange-200 text-orange-800' :
                            item.alertLevel === 'low' ? 'bg-yellow-200 text-yellow-800' :
                            'bg-green-200 text-green-800'
                          }`}>
                            {item.alertLevel}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                          <div>Score: <strong>{(item.anomalyScore * 100).toFixed(0)}%</strong></div>
                          <div>Confidence: <strong>{(item.confidence * 100).toFixed(0)}%</strong></div>
                          <div>Expected: <strong>{item.expectedLocation || '-'}</strong></div>
                        </div>
                        {item.createdAt && (
                          <div className="text-xs text-gray-400 mt-2">
                            {new Date(item.createdAt).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No anomaly history found.</p>
                )}
              </div>
            )}
          </div>

          {/* Quick Reference */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">Alert Level Thresholds</h4>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>None: 0-30%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span>Low: 30-50%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span>Medium: 50-70%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>High: 70-100%</span>
              </div>
            </div>
            <h4 className="font-semibold text-blue-800 mt-4 mb-2">Anomaly Types</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
              <div><strong>Temporal:</strong> At known location but wrong time</div>
              <div><strong>Spatial:</strong> At unexpected location</div>
              <div><strong>Route:</strong> Unusual travel path</div>
              <div><strong>Behavioral:</strong> Unusual movement pattern</div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION 3: DANGEROUS AREAS ==================== */}
      <section id="dangerous-areas">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          Dangerous Areas
          <FeatureInfoButton feature="zones" label="Docs" />
        </h2>
        <div className="space-y-6">
          {personId ? (
            <>
              {/* Top Row - Summary and History */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ZoneExposureSummary alienId={personId} />
                <ZoneVisitHistory alienId={personId} days={30} maxItems={15} />
              </div>

              {/* Map - Full Width */}
              <ZoneVisitMap alienId={personId} days={30} height={500} />
            </>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <h3 className="text-xl font-semibold mb-2">Dangerous Areas</h3>
              <p className="text-gray-500 mb-4">
                Enter a Person ID above to view their high-risk zone exposure,
                visit history, and zone map visualization.
              </p>
            </div>
          )}

          {/* High-Risk Zones Reference */}
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <h4 className="font-semibold text-red-800 mb-2">Risk Levels</h4>
            <div className="grid grid-cols-5 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>1 - Low</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span>2 - Guarded</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span>3 - Elevated</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>4 - High</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-800 rounded"></div>
                <span>5 - Critical</span>
              </div>
            </div>
            <h4 className="font-semibold text-red-800 mt-4 mb-2">Zone Categories</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
              <div><strong>Known Stash House:</strong> Identified drug/contraband storage</div>
              <div><strong>Gang Territory:</strong> Areas with gang activity</div>
              <div><strong>Smuggling Corridor:</strong> Known smuggling routes</div>
              <div><strong>Previous Arrest Location:</strong> Past arrest sites</div>
              <div><strong>Associated Criminal Address:</strong> Known associate locations</div>
              <div><strong>Border Crossing Point:</strong> Unmonitored border areas</div>
              <div><strong>Flight Risk Location:</strong> Airports, bus stations, etc.</div>
              <div><strong>Monitored Residence:</strong> Under surveillance locations</div>
            </div>
            <h4 className="font-semibold text-red-800 mt-4 mb-2">Exposure Score Factors</h4>
            <div className="grid grid-cols-3 gap-2 text-sm text-gray-700">
              <div><strong>Visit Frequency:</strong> Weighted by risk level (40%)</div>
              <div><strong>Dwell Time:</strong> Time spent in zones (30%)</div>
              <div><strong>POL Overlap:</strong> Routine locations in zones (30%)</div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION 4: CONNECTIONS ==================== */}
      <section id="connections">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          Connections
          <FeatureInfoButton feature="network" label="Docs" />
        </h2>
        <div className="space-y-6">
          {/* Feature Header */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <span className="text-cyan-600">Part 3:</span> Association & Network Intelligence
              <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-1 rounded">Co-location Analysis</span>
            </h3>
            <p className="text-gray-600 text-sm">
              Analyze subject associations through co-location patterns. Build network graphs from shared
              time-and-place events, identify communities, calculate centrality metrics, and assess
              network-based association risk.
            </p>
          </div>

          {/* Network Intelligence Panel */}
          <NetworkIntelligencePanel alienId={personId ? parseInt(personId) : null} />

          {/* Network Intelligence Reference */}
          <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
            <h4 className="font-semibold text-cyan-800 mb-2">Network Intelligence Reference</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h5 className="font-medium text-cyan-700 mb-2">Centrality Metrics</h5>
                <ul className="space-y-1 text-gray-700">
                  <li><strong>Degree Centrality:</strong> Direct connections count</li>
                  <li><strong>Betweenness:</strong> Bridge role between groups</li>
                  <li><strong>PageRank:</strong> Connection quality importance</li>
                  <li><strong>Eigenvector:</strong> Influence through neighbors</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-cyan-700 mb-2">Risk Levels</h5>
                <ul className="space-y-1 text-gray-700">
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#22C55E' }}></span> <strong>Low:</strong> 0-25% risk score</li>
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#F59E0B' }}></span> <strong>Medium:</strong> 25-50% risk score</li>
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#EF4444' }}></span> <strong>High:</strong> 50-75% risk score</li>
                  <li><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#991B1B' }}></span> <strong>Critical:</strong> 75-100% risk score</li>
                </ul>
                <h5 className="font-medium text-cyan-700 mt-4 mb-2">Connection Types</h5>
                <ul className="space-y-1 text-gray-700">
                  <li><span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1"></span> <strong>Focal Subject</strong></li>
                  <li><span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-1"></span> <strong>Direct Connection (Depth 1)</strong></li>
                  <li><span className="inline-block w-3 h-3 rounded-full bg-pink-500 mr-1"></span> <strong>Indirect Connection (Depth 2+)</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PolRiskTab;
